import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'components/Notification'
import {
  getTearsheetCustomTemplates,
  createTearsheetCustomTemplate,
  updateTearsheetCustomTemplate,
  deleteTearsheetCustomTemplate,
} from 'services/tearsheets'

const useTearsheetTemplates = customer => {
  const customerId = customer?.id
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()

  const { data: tearsheetTemplates, isFetching: isFetchingTearsheetTemplates } = useQuery({
    queryFn: () => getTearsheetCustomTemplates({ customerId }),
    queryKey: ['getTearsheetCustomTemplates', customerId],
    enabled: !!customerId,
    initialData: [],
  })

  const { mutate: createTemplate, isLoading: isCreateTemplateLoading } = useMutation({
    mutationFn: params => createTearsheetCustomTemplate({ ...params, customerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTearsheetCustomTemplates', customerId] })
      createNotification({
        message: 'Template created successfully',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Failed to create template',
        variant: 'error',
      })
    },
  })

  const { mutate: updateTemplate, isLoading: isUpdateTemplateLoading } = useMutation({
    mutationFn: updateTearsheetCustomTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTearsheetCustomTemplates', customerId] })
      createNotification({
        message: 'Template updated successfully',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Failed to update template',
        variant: 'error',
      })
    },
  })

  const { mutate: deleteTemplate, isLoading: isDeleteTemplateLoading } = useMutation({
    mutationFn: deleteTearsheetCustomTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getTearsheetCustomTemplates', customerId] })
      createNotification({
        message: 'Template deleted successfully',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Failed to delete template',
        variant: 'error',
      })
    },
  })

  return {
    tearsheetTemplates,
    isFetchingTearsheetTemplates,
    createTemplate,
    isCreateTemplateLoading,
    updateTemplate,
    isUpdateTemplateLoading,
    deleteTemplate,
    isDeleteTemplateLoading,
  }
}

export default useTearsheetTemplates
